import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/UpdatedMainLogo.png';
import getApiBase from '../../components/getApiBase';
import PlaceIdRepairModal from '../../components/PlaceIdRepairModal';

const Header = ({ onLogout }) => {
  const navigate = useNavigate();
  
  return (
    <header style={{
      backgroundColor: '#fff',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
      padding: '12px 24px',
      position: 'sticky',
      top: 0,
      zIndex: 1000
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <img src={logo} alt="Autoshops.com Logo" style={{ height: 'auto', width: '280px' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/admin')}
            style={{
              color: '#ea4335',
              fontWeight: 700,
              fontSize: '18px',
              fontFamily: 'Poppins, sans-serif',
              padding: '10px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'none'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#fee2e2';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            ← Back to Dashboard
          </button>
          <span style={{ 
            color: '#000', 
            fontWeight: 700,
            fontSize: '18px',
            fontFamily: 'Poppins, sans-serif'
          }}>
            DB Management
          </span>
          <button
            onClick={onLogout}
            style={{
              backgroundColor: '#4285f4',
              color: 'white',
              borderRadius: '6px',
              padding: '6px 16px',
              fontSize: '14px',
              fontWeight: 600,
              fontFamily: 'Poppins, sans-serif',
              border: 'none',
              cursor: 'pointer',
              boxShadow: 'none'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#1d4ed8';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#4285f4';
            }}
          >
            Logout
          </button>
        </div>
      </div>
    </header>
  );
};

// Feature Card Component
const FeatureCard = ({ icon, title, description, onClick, status = 'coming-soon' }) => {
  const isActive = status === 'active';
  
  return (
    <div 
      onClick={isActive ? onClick : undefined}
      style={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '20px',
        cursor: isActive ? 'pointer' : 'not-allowed',
        opacity: isActive ? 1 : 0.6,
        transition: 'all 0.2s',
        ...(isActive && {
          ':hover': {
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            borderColor: '#2563eb'
          }
        })
      }}
      onMouseOver={(e) => {
        if (isActive) {
          e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
          e.currentTarget.style.borderColor = '#2563eb';
          e.currentTarget.style.transform = 'scale(1.02)';
        }
      }}
      onMouseOut={(e) => {
        if (isActive) {
          e.currentTarget.style.boxShadow = 'none';
          e.currentTarget.style.borderColor = '#e5e7eb';
          e.currentTarget.style.transform = 'scale(1)';
        }
      }}
    >
      <div style={{ fontSize: '32px', marginBottom: '12px' }}>
        {icon}
      </div>
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: '#111827',
        marginBottom: '8px',
        fontFamily: 'Poppins, sans-serif'
      }}>
        {title}
      </h3>
      <p style={{
        fontSize: '14px',
        color: '#6b7280',
        lineHeight: '1.5',
        fontFamily: 'Poppins, sans-serif',
        margin: 0
      }}>
        {description}
      </p>
      {isActive && (
        <div style={{
          marginTop: '12px',
          fontSize: '20px',
          color: '#9ca3af',
          textAlign: 'right'
        }}>
          ›
        </div>
      )}
    </div>
  );
};

const DBManagement = () => {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isPlaceIdModalOpen, setIsPlaceIdModalOpen] = useState(false);

  // Check if user has developer role (in primary OR secondary role)
  useEffect(() => {
    const checkDeveloperRole = async () => {
      try {
        // Get current user info
        const userResponse = await fetch(`${getApiBase()}/api/auth/me`, {
          credentials: 'include'
        });
        
        if (!userResponse.ok) {
          navigate('/login');
          return;
        }
        
        const userData = await userResponse.json();
        const userEmail = userData.user?.email;
        
        if (!userEmail) {
          navigate('/admin');
          return;
        }
        
        // Get all admins to find current user's roles
        const response = await fetch(`${getApiBase()}/api/admin-management/roles`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          // Find current user in the admins list
          const currentUser = data.admins?.find((admin) => admin.email === userEmail);
          
          if (currentUser && currentUser.admin_roles) {
            const activeRoles = currentUser.admin_roles.filter((role) => role.is_active !== false);
            
            // Check if Developer is in primary OR secondary role
            // Primary role: role with primary_role === true
            // Secondary role: role with primary_role === false or undefined
            const hasDeveloperInPrimary = activeRoles.some(
              (role) => role.role_type === 'developer' && role.primary_role === true
            );
            const hasDeveloperInSecondary = activeRoles.some(
              (role) => role.role_type === 'developer' && (role.primary_role === false || !role.primary_role)
            );
            
            // Also allow super_admin and admin roles (they have developer access)
            const hasSuperAdminOrAdmin = activeRoles.some(
              (role) => ['super_admin', 'admin'].includes(role.role_type)
            );
            
            const isDeveloper = hasDeveloperInPrimary || hasDeveloperInSecondary || hasSuperAdminOrAdmin;
            
            if (isDeveloper) {
              setIsAuthorized(true);
            } else {
              // Not a developer - redirect to admin dashboard
              navigate('/admin');
            }
          } else {
            // No admin roles found
            navigate('/admin');
          }
        } else {
          // Not an admin
          navigate('/admin');
        }
      } catch (error) {
        console.error('Error checking developer role:', error);
        navigate('/admin');
      } finally {
        setLoading(false);
      }
    };
    
    checkDeveloperRole();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await fetch(`${getApiBase()}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      localStorage.removeItem("user");
      navigate('/login');
    } catch (err) {
      console.error("Logout failed:", err);
      navigate('/login');
    }
  };

  // Show loading state while checking authorization
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <Header onLogout={handleLogout} />
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
          fontFamily: 'Poppins, sans-serif',
          fontSize: '18px',
          color: '#6b7280'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  // Don't render if not authorized (will redirect in useEffect)
  if (!isAuthorized) {
    return null;
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Header onLogout={handleLogout} />
      
      <div style={{
        padding: '40px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '40px'
        }}>
          <h1 style={{
            fontSize: '32px',
            fontWeight: 'bold',
            color: '#1f2937',
            fontFamily: 'Poppins, sans-serif',
            margin: 0
          }}>
            🔧 Database Management
          </h1>
          
          <button
            onClick={() => navigate('/admin')}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 500
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#4b5563';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#6b7280';
            }}
          >
            ← Back to Admin
          </button>
        </div>

        {/* Coming Soon Section */}
        <div style={{
          backgroundColor: '#f9fafb',
          border: '2px dashed #d1d5db',
          borderRadius: '12px',
          padding: '60px 40px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '80px',
            marginBottom: '20px'
          }}>
            🚧
          </div>
          
          <h2 style={{
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#374151',
            marginBottom: '16px',
            fontFamily: 'Poppins, sans-serif',
            marginTop: 0
          }}>
            Coming Soon
          </h2>
          
          <p style={{
            fontSize: '16px',
            color: '#6b7280',
            marginBottom: '32px',
            maxWidth: '600px',
            margin: '0 auto 32px',
            fontFamily: 'Poppins, sans-serif',
            lineHeight: '1.5'
          }}>
            Advanced database management tools are under development. This page will include:
          </p>

          {/* Feature List */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px',
            marginTop: '40px',
            textAlign: 'left'
          }}>
            <FeatureCard 
              icon="🔧"
              title="Repair Shop place_id"
              description="Fix incorrect Google Place IDs for shops"
              status="active"
              onClick={() => setIsPlaceIdModalOpen(true)}
            />
            
            <FeatureCard 
              icon="📊"
              title="Database Statistics"
              description="View comprehensive stats about shops, mechanics, and claims"
            />
            
            <FeatureCard 
              icon="✅"
              title="Data Integrity Checks"
              description="Detect duplicates, orphaned records, and data issues"
            />
            
            <FeatureCard 
              icon="🔄"
              title="Bulk Operations"
              description="Refresh shop data, sync reviews, and update ratings in bulk"
            />
            
            <FeatureCard 
              icon="📜"
              title="Operation Logs"
              description="Track history of all database maintenance operations"
            />
            
            <FeatureCard 
              icon="🔀"
              title="Data Management"
              description="Merge duplicates, export/import data, and more"
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      <PlaceIdRepairModal 
        isOpen={isPlaceIdModalOpen} 
        onClose={() => setIsPlaceIdModalOpen(false)} 
      />
    </div>
  );
};

export default DBManagement;

